import { useState, useEffect } from 'react'
import { directorApi, type DirectorDelegationItem, type FacultyWithDirectors, type SchoolDirectorItem } from '@/services/api/director'
import type { DirectorConfig } from '@/types/director'
import { extractErrorMessage } from '@/utils/errorHandler'
import { Loader2, Plus, X, ArrowRightLeft } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'

export function DirectorDelegationManager() {
  const { user } = useAuthStore()
  const isSuperadmin = user?.role === 'superadmin'
  const [delegations, setDelegations] = useState<DirectorDelegationItem[]>([])
  const [faculties, setFaculties] = useState<FacultyWithDirectors[]>([])
  const [schoolDirectors, setSchoolDirectors] = useState<SchoolDirectorItem[]>([])
  const [canGrantSchoolWide, setCanGrantSchoolWide] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [revokingIds, setRevokingIds] = useState<Set<string>>(new Set())

  const [selectedFacultyId, setSelectedFacultyId] = useState('')
  const [selectedDelegateId, setSelectedDelegateId] = useState('')
  const [selectedDelegatingId, setSelectedDelegatingId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isPermanent, setIsPermanent] = useState(false)
  const [notes, setNotes] = useState('')

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [delegationsRes, facultiesRes, directorsRes, configRes] = await Promise.all([
        directorApi.getDelegations(),
        directorApi.getFacultiesWithDirectors(),
        directorApi.getSchoolDirectors(),
        directorApi.getConfig().catch(() => null as DirectorConfig | null),
      ])
      setDelegations(delegationsRes)
      setFaculties(facultiesRes)
      setSchoolDirectors(directorsRes)
      setCanGrantSchoolWide(
        isSuperadmin
        || (configRes?.can_delegate === true && configRes.visibility_scope === 'school'),
      )
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Daten konnten nicht geladen werden' }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [])

  const handleCreate = async () => {
    if (!selectedDelegateId) return
    if (!canGrantSchoolWide && !selectedFacultyId) return
    setSubmitting(true)
    setError(null)
    try {
      await directorApi.createDelegation({
        faculty_id: selectedFacultyId || undefined,
        delegate_director_id: selectedDelegateId,
        delegating_director_id: selectedDelegatingId || undefined,
        start_date: startDate || undefined,
        end_date: isPermanent ? undefined : (endDate || undefined),
        notes: notes || undefined,
      })
      setSuccess('Stellvertretung erfolgreich erstellt')
      setShowCreateModal(false)
      resetForm()
      fetchData()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Stellvertretung konnte nicht erstellt werden' }))
    } finally {
      setSubmitting(false)
    }
  }

  const handleRevoke = async (delegationId: string) => {
    setRevokingIds(prev => new Set([...prev, delegationId]))
    setError(null)
    try {
      await directorApi.revokeDelegation(delegationId)
      setSuccess('Stellvertretung widerrufen')
      fetchData()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Stellvertretung konnte nicht widerrufen werden' }))
    } finally {
      setRevokingIds(prev => {
        const next = new Set(prev)
        next.delete(delegationId)
        return next
      })
    }
  }

  const resetForm = () => {
    setSelectedFacultyId('')
    setSelectedDelegateId('')
    setSelectedDelegatingId('')
    setStartDate('')
    setEndDate('')
    setIsPermanent(false)
    setNotes('')
  }

  const getStatusBadge = (d: DirectorDelegationItem) => {
    if (!d.is_active) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inaktiv</span>
    }
    if (d.start_date && new Date(d.start_date) > new Date()) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Geplant</span>
    }
    if (d.end_date && new Date(d.end_date) < new Date()) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Abgelaufen</span>
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Aktiv</span>
  }

  const formatDate = (d: string | null) => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('de-CH')
  }

  const delegatingId = selectedDelegatingId || user?.id
  const allDirectors = schoolDirectors.filter(d => d.id !== delegatingId)
  const delegatingCandidates = schoolDirectors.filter(d => d.id !== selectedDelegateId)

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => { resetForm(); setShowCreateModal(true) }}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 gap-2 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Neue Stellvertretung
        </button>
      </div>

      {success && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700"><X className="w-4 h-4" /></button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : delegations.length === 0 ? (
        <div className="bg-white rounded-lg border p-8 text-center">
          <ArrowRightLeft className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Keine Stellvertretungen</h3>
          <p className="text-sm text-gray-500 mb-4">
            Es wurden noch keine Stellvertretungen konfiguriert.
          </p>
          <button
            onClick={() => { resetForm(); setShowCreateModal(true) }}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 gap-2 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Erste Stellvertretung erstellen
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fakultat</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vertritt</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stellvertretung</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zeitraum</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktion</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {delegations.map(d => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{d.faculty_name || 'Alle Fakultäten'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-600">{d.delegating_director_name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-600">{d.delegate_director_name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-600">
                      {d.start_date || d.end_date ? (
                        <span>{formatDate(d.start_date)} – {formatDate(d.end_date)}</span>
                      ) : (
                        <span className="text-gray-400">Dauerhaft</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(d)}
                  </td>
                  <td className="px-4 py-3">
                    {d.is_currently_active && (
                      <button
                        onClick={() => handleRevoke(d.id)}
                        disabled={revokingIds.has(d.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                      >
                        {revokingIds.has(d.id) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Widerrufen'
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Neue Stellvertretung</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {canGrantSchoolWide ? 'Fakultät (optional)' : 'Fakultät'}
                </label>
                <select
                  value={selectedFacultyId}
                  onChange={e => setSelectedFacultyId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {canGrantSchoolWide && <option value="">Alle Fakultäten</option>}
                  {!canGrantSchoolWide && <option value="">Fakultät wählen...</option>}
                  {faculties.map(f => (
                    <option key={f.id} value={f.id}>{f.name} ({f.directors.length} Direktoren)</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stellvertretender Direktor</label>
                <select
                  value={selectedDelegateId}
                  onChange={e => setSelectedDelegateId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Direktor wählen...</option>
                  {allDirectors.map(d => (
                    <option key={d.id} value={d.id}>{d.name} ({d.email})</option>
                  ))}
                </select>
                {allDirectors.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">Keine anderen Direktoren verfügbar.</p>
                )}
              </div>

              {isSuperadmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delegierender Direktor (optional)</label>
                  <select
                    value={selectedDelegatingId}
                    onChange={e => setSelectedDelegatingId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Selbst (Standard)</option>
                    {delegatingCandidates.map(d => (
                      <option key={d.id} value={d.id}>{d.name} ({d.email})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="permanent"
                  checked={isPermanent}
                  onChange={e => setIsPermanent(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <label htmlFor="permanent" className="text-sm text-gray-700">Dauerhafte Stellvertretung</label>
              </div>

              {!isPermanent && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Startdatum</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Enddatum</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notizen (optional)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="z.B. Grund der Stellvertretung..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreate}
                disabled={submitting || !selectedDelegateId || (!canGrantSchoolWide && !selectedFacultyId)}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
